import { useMemo, useState } from "react";
import { Tabs } from "../../../shared/Tabs";
import { SettingsHeader } from "../index";
import { Modal } from "../../../shared";
import {
  useDeleteTemplateMutation,
  useGetTemplatesQuery,
} from "../../../redux/services/templates/api";
import { toast } from "react-toastify";
import { useConfig } from "../../../hooks";
import { capitalizeFirstLetter } from "../../../utils";
import PDFTemplates from "./PDFTemplates";
import TaskTemplates from "../TaskTemplates";
import TemplateListPanel from "./TemplateListPanel";
import JobTemplateModal from "./JobTemplateModal";
import LegTemplateModal from "./LegTemplateModal";
import { TEMPLATE_TYPES } from "./template.constants";
import { templateMatchesAppType } from "./template.utils";

const Templates = () => {
  const { appType } = useConfig();
  const [modalState, setModalState] = useState({
    visible: false,
    type: TEMPLATE_TYPES.JOB,
    mode: "create",
    template: null,
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmContent, setConfirmContent] = useState(null);

  const { data, isLoading, isError } = useGetTemplatesQuery({ limit: 100 });
  const [deleteTemplate] = useDeleteTemplateMutation();

  const groupedTemplates = useMemo(() => {
    return (data?.templates || []).filter((template) =>
      templateMatchesAppType(template, appType)
    ).reduce((acc, template) => {
      acc[template.type] = [...(acc[template.type] || []), template];
      return acc;
    }, {});
  }, [data?.templates, appType]);

  function openTemplateModal(type, mode = "create", template = null) {
    setModalState({
      visible: true,
      type,
      mode,
      template,
    });
  }

  function handleConfirmModal(type, template) {
    setConfirmContent({
      title: `${capitalizeFirstLetter(type)} Template`,
      content: `Are you sure you want to ${type} this template?`,
      template,
      type,
    });
    setShowConfirmModal(true);
  }

  async function handleDeleteTemplate(template) {
    try {
      const response = await deleteTemplate(template._id).unwrap();
      if (response.status !== "success") {
        toast.error("Failed to delete template");
        return;
      }
      toast.success("Template deleted successfully");
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error(
        error.message || "An error occurred while deleting the template",
      );
    }
  }

  function handleConfirm(type) {
    if (type === "delete") {
      handleDeleteTemplate(confirmContent.template);
    }
    setShowConfirmModal(false);
    handleCleanUp();
  }

  function handleCleanUp() {
    setConfirmContent(null);
    setModalState({
      visible: false,
      type: TEMPLATE_TYPES.JOB,
      mode: "create",
      template: null,
    });
  }

  const renderOperationalPanel = ({
    type,
    title,
    subtitle,
    emptyDescription,
  }) => (
    <TemplateListPanel
      title={title}
      subtitle={subtitle}
      templates={groupedTemplates[type] || []}
      isLoading={isLoading}
      isError={isError}
      addLabel={`Add ${type === TEMPLATE_TYPES.LEG ? "Leg" : "Job"} Template`}
      emptyDescription={emptyDescription}
      onAdd={() => openTemplateModal(type)}
      onEdit={(template) => openTemplateModal(type, "edit", template)}
      onDuplicate={(template) => openTemplateModal(type, "duplicate", template)}
      onDelete={(template) => handleConfirmModal("delete", template)}
    />
  );

  const tabsData = [
    {
      id: "job",
      title: "Job Templates",
      content: renderOperationalPanel({
        type: TEMPLATE_TYPES.JOB,
        title: appType === "trucking" ? "Load Templates" : "Job Templates",
        subtitle:
          appType === "trucking"
            ? "Reuse load defaults, rates, and optional trucking checklist groups."
            : "Reuse job defaults, service items, rates, and optional checklist groups.",
        emptyDescription:
          appType === "trucking"
            ? "Create a load template to prefill repeatable trucking work."
            : "Create a job template to prefill repeatable service work.",
      }),
    },
    {
      id: "leg",
      title: appType === "trucking" ? "Load Leg Templates" : "Leg Templates",
      content: renderOperationalPanel({
        type: TEMPLATE_TYPES.LEG,
        title: appType === "trucking" ? "Load Leg Templates" : "Leg Templates",
        subtitle:
          appType === "trucking"
            ? "Reuse pickup, delivery, mileage, charge, schedule, and trucking checklist defaults."
            : "Reuse route, charge, schedule, and checklist defaults for legs.",
        emptyDescription:
          appType === "trucking"
            ? "Create a leg template to speed up common pickup, delivery, or return loads."
            : "Create a leg template to speed up repeatable work segments.",
      }),
    },
    // {
    //   id: "tasks",
    //   title: "Task & Checklist Templates",
    //   content: <TaskTemplates embedded />,
    // },
    // {
    //   id: "pdf",
    //   title: "PDF Templates",
    //   content: <PDFTemplates />,
    // },
  ];

  return (
    <>
      <SettingsHeader title="Templates" />

      <div className="">
        <Tabs
          useSearchParams
          data={tabsData}
          default={0}
          fullHeight={false}
          tabContentClassName={(isActive) =>
            `inline-flex p-2 group focus:outline-none rounded-lg text-sm font-semibold focus-visible:outline-0 ${
              isActive
                ? "text-primary-600 bg-primary-50 dark:text-primary-500 dark:bg-primary-900/20 border-transparent"
                : "hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300 text-gray-700 border-transparent"
            }`
          }
          panelClasses="mt-2"
          tabListClasses="mb-1"
        />
      </div>

      <JobTemplateModal
        visible={modalState.visible && modalState.type === TEMPLATE_TYPES.JOB}
        template={modalState.template}
        mode={modalState.mode}
        onClose={handleCleanUp}
      />
      <LegTemplateModal
        visible={modalState.visible && modalState.type === TEMPLATE_TYPES.LEG}
        template={modalState.template}
        mode={modalState.mode}
        onClose={handleCleanUp}
      />
      <Modal
        title={confirmContent?.title}
        visible={showConfirmModal}
        setVisible={setShowConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        confirm={() => handleConfirm(confirmContent?.type)}
      >
        <div>{confirmContent?.content}</div>
      </Modal>
    </>
  );
};

export default Templates;
